
import React from 'react';
import { FieldErrors } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Calendar, Users, TrendingUp, Globe } from 'lucide-react';
import { EditProspectFormData } from './types';
import { motion } from 'framer-motion';

interface ProspectFormProps {
  register: any;
  errors: FieldErrors<EditProspectFormData>;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
}

const ProspectForm: React.FC<ProspectFormProps> = ({
  register,
  errors,
  onSubmit
}) => {
  // Form fields defined but not currently used
  /*const formFields = [
    {
      id: 'name',
      label: 'Company Name',
      icon: null,
      required: true,
      type: 'text',
      placeholder: 'Enter company name',
      fieldType: 'input'
    },
    {
      id: 'description',
      label: 'Description',
      icon: null,
      required: false,
      type: 'textarea',
      placeholder: 'Enter company description',
      fieldType: 'textarea'
    },
    {
      id: 'location',
      label: 'Location',
      icon: MapPin,
      required: false,
      type: 'text',
      placeholder: 'Enter location',
      fieldType: 'input'
    },
    {
      id: 'industry',
      label: 'Industry',
      icon: Calendar,
      required: false,
      type: 'text',
      placeholder: 'Enter industry',
      fieldType: 'input'
    },
    {
      id: 'employee_count',
      label: 'Employee Count',
      icon: Users,
      required: false,
      type: 'number',
      placeholder: 'Enter employee count',
      fieldType: 'input'
    },
    {
      id: 'revenue_range',
      label: 'Revenue Range',
      icon: TrendingUp,
      required: false,
      type: 'text',
      placeholder: 'Enter revenue range',
      fieldType: 'input'
    },
    {
      id: 'website',
      label: 'Website',
      icon: Globe,
      required: false,
      type: 'text',
      placeholder: 'Enter website URL',
      fieldType: 'input'
    }
  ];*/

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Company Name and Description */}
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-2"
        >
          <Label htmlFor="name" className="text-sm font-medium">Company Name</Label>
          <Input
            id="name"
            {...register('name', { required: 'Company name is required' })}
            placeholder="Enter company name"
            className={`bg-accent/5 border ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-accent/20 focus:border-blue-500 focus:ring-blue-500'} transition-all duration-200`}
          />
          {errors.name && (
            <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2"
        >
          <Label htmlFor="description" className="text-sm font-medium">Description</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Enter company description"
            className="min-h-[100px] bg-accent/5 border border-accent/20 focus:border-blue-500 focus:ring-blue-500 transition-all duration-200"
          />
        </motion.div>
      </div>

      {/* Details Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        {/* Location */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-blue-400" />
            <Label htmlFor="location" className="text-sm font-medium">Location</Label>
          </div>
          <Input
            id="location"
            {...register('location')}
            placeholder="Enter location"
            className="bg-accent/5 border border-accent/20 focus:border-blue-500 focus:ring-blue-500 transition-all duration-200"
          />
        </div>

        {/* Industry */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-indigo-400" />
            <Label htmlFor="industry" className="text-sm font-medium">Industry</Label>
          </div>
          <Input
            id="industry"
            {...register('industry')}
            placeholder="Enter industry"
            className="bg-accent/5 border border-accent/20 focus:border-blue-500 focus:ring-blue-500 transition-all duration-200"
          />
        </div>

        {/* Employee Count */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-violet-400" />
            <Label htmlFor="employee_count" className="text-sm font-medium">Employee Count</Label>
          </div>
          <Input
            id="employee_count"
            type="number"
            {...register('employee_count', { valueAsNumber: true })}
            placeholder="Enter employee count"
            className="bg-accent/5 border border-accent/20 focus:border-blue-500 focus:ring-blue-500 transition-all duration-200"
          />
        </div>

        {/* Revenue Range */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-emerald-400" />
            <Label htmlFor="revenue_range" className="text-sm font-medium">Revenue Range</Label>
          </div>
          <Input
            id="revenue_range"
            {...register('revenue_range')}
            placeholder="Enter revenue range"
            className="bg-accent/5 border border-accent/20 focus:border-blue-500 focus:ring-blue-500 transition-all duration-200"
          />
        </div>

        {/* Website */}
        <div className="space-y-2 col-span-1 md:col-span-2">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-cyan-400" />
            <Label htmlFor="website" className="text-sm font-medium">Website</Label>
          </div>
          <Input
            id="website"
            {...register('website')}
            placeholder="Enter website URL"
            className="bg-accent/5 border border-accent/20 focus:border-blue-500 focus:ring-blue-500 transition-all duration-200"
          />
        </div>
      </motion.div>
    </form>
  );
};

export default ProspectForm;
